import { BOTTOM_NAV_HEIGHT } from "../BottomNavBar";
import { useAuthStore } from "../../store/authStore";
import { useListStore } from "../../store/listStore";
import { usePlaceStore } from "../../store/placeStore";
import { usePhotoStore } from "../../store/photoStore";

import { useShallow } from "zustand/react/shallow";
import { useGetGooglePlaceDetails } from "../../api/use-get-google-place-details";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import Rating from "@mui/material/Rating";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Stack,
  Typography,
  useTheme,
  Divider,
  IconButton,
  Chip,
  Input,
  Textarea,
  Modal,
  ModalDialog,
  ModalClose,
  Button,
  Select,
  Option,
  FormControl,
  FormLabel,
  CircularProgress,
  Snackbar,
  Checkbox,
  List,
  ListItem,
} from "@mui/joy";
import React from "react";
import { Database } from "../../lib/database";
import { extractExifData } from "../../utils/exifUtils";
import { MapState } from "../../hooks/useMapState";
import { PhotoFile, PhotoItem } from "../../types";
import { CreateListModal } from "../CreateListModal";
import { useIsMobile } from "../../hooks/useIsMobile";
import { PhotoUpload } from "../photos/PhotoUpload";

type Place = Database["public"]["Tables"]["places"]["Row"];
type Photo = Database["public"]["Tables"]["photos"]["Row"];

type PlaceDetailProps =
  | { mode: "view"; listPlaceId: string; listId?: string; mapState: MapState }
  | {
      mode: "add";
      googlePlaceId: string;
      mapState: MapState;
      onSaveComplete: () => void;
    };

export const PlaceDetail = (props: PlaceDetailProps) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { profile } = useAuthStore();

  const { lists, fetchLists, createList } = useListStore(
    useShallow((state) => ({
      lists: state.lists,
      fetchLists: state.fetchLists,
      createList: state.createList,
    })),
  );
  const {
    listPlaces,
    places,
    fetchPlace,
    fetchPlaceById,
    findPlaceByGoogleId,
    createPlace,
    updatePlace,
    addPlaceToList,
  } = usePlaceStore(
    useShallow((state) => ({
      listPlaces: state.listPlaces,
      places: state.places,
      fetchPlace: state.fetchPlace,
      fetchPlaceById: state.fetchPlaceById,
      findPlaceByGoogleId: state.findPlaceByGoogleId,
      createPlace: state.createPlace,
      updatePlace: state.updatePlace,
      addPlaceToList: state.addPlaceToList,
    })),
  );
  const {
    fetchPhotosForPlace,
    fetchPhotosForPlaceByOwner,
    uploadPhoto,
    deletePhoto,
  } = usePhotoStore(
    useShallow((state) => ({
      fetchPhotosForPlace: state.fetchPhotosForPlace,
      fetchPhotosForPlaceByOwner: state.fetchPhotosForPlaceByOwner,
      uploadPhoto: state.uploadPhoto,
      deletePhoto: state.deletePhoto,
    })),
  );

  // Common state - combined to reduce setState calls in effects
  const [placeData, setPlaceData] = React.useState<{
    place: Place | null;
    rating: number;
    tags: string[];
    notes: string;
  }>({ place: null, rating: 0, tags: [], notes: "" });
  const { place, rating, tags, notes } = placeData;
  const [newTag, setNewTag] = React.useState("");
  const [isAddingTag, setIsAddingTag] = React.useState(false);
  const [photos, setPhotos] = React.useState<Photo[]>([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [slideshowOpen, setSlideshowOpen] = React.useState(false);
  const [slideshowIndex, setSlideshowIndex] = React.useState(0);

  // Add mode specific state
  const [selectedLists, setSelectedLists] = React.useState<string[]>([]);
  const [createListModalOpen, setCreateListModalOpen] = React.useState(false);
  const [pendingPhotos, setPendingPhotos] = React.useState<PhotoFile[]>([]);
  const [pendingPhotoIndex, setPendingPhotoIndex] = React.useState(0);
  const [isSaving, setIsSaving] = React.useState(false);

  // Read-only mode specific state (viewing another user's place)
  const [showListSelector, setShowListSelector] = React.useState(false);
  const [readOnlySelectedLists, setReadOnlySelectedLists] = React.useState<
    string[]
  >([]);
  const [isAddingToMyLists, setIsAddingToMyLists] = React.useState(false);
  const [createListForSaveModalOpen, setCreateListForSaveModalOpen] =
    React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [isLoadingPlace, setIsLoadingPlace] = React.useState(true);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const isViewMode = props.mode === "view";
  const isAddMode = props.mode === "add";
  const { setMapPlaceId, setMapCenter } = props.mapState;

  // Get the listPlace for view mode
  const listPlace = isViewMode
    ? listPlaces.find((p) => p.id === props.listPlaceId)
    : null;

  // Derive read-only state from listPlace ownership
  const listId = props.mode === "view" ? props.listId : undefined;
  const ownerId = isViewMode && listPlace ? listPlace.user_id : undefined;
  const isReadOnly = !!(listId && ownerId && ownerId !== profile?.id);

  // Refs to stabilize effect dependencies
  const setMapPlaceIdRef = React.useRef(setMapPlaceId);
  const setMapCenterRef = React.useRef(setMapCenter);

  // Update refs on each render
  React.useLayoutEffect(() => {
    setMapPlaceIdRef.current = setMapPlaceId;
    setMapCenterRef.current = setMapCenter;
  });

  // Fetch Google place details for add mode
  const { data: googlePlaceDetails, isLoading: isLoadingGoogleDetails } =
    useGetGooglePlaceDetails(isAddMode ? props.googlePlaceId : null);

  // Fetch lists for add mode or when read-only list selector opens
  React.useEffect(() => {
    if (isAddMode && profile) {
      fetchLists(profile!.id);
    }
  }, [isAddMode, profile, fetchLists]);

  React.useEffect(() => {
    if (showListSelector && profile) {
      fetchLists(profile.id);
    }
  }, [showListSelector, profile, fetchLists]);

  // Update map center when Google place details are loaded (add mode)
  React.useEffect(() => {
    if (isAddMode && googlePlaceDetails) {
      setMapCenterRef.current?.({
        lat: googlePlaceDetails.location.latitude,
        lng: googlePlaceDetails.location.longitude,
      });
    }
  }, [isAddMode, googlePlaceDetails]);

  // Track initialized place to avoid re-initializing
  const initializedPlaceIdRef = React.useRef<string | null>(null);

  // Reset initializedPlaceId when leaving view mode
  if (!isViewMode && initializedPlaceIdRef.current !== null) {
    initializedPlaceIdRef.current = null;
  }

  // Initialize place data and fetch photos when viewing
  React.useEffect(() => {
    if (!isViewMode || !profile || !listPlace) return;
    if (initializedPlaceIdRef.current === listPlace.id) return;

    if (isReadOnly) {
      // Read-only path: fetch place by ID and photos by owner
      if (!ownerId) return;

      let cancelled = false;
      setIsLoadingPlace(true);

      const loadData = async () => {
        try {
          const fetchedPlace = await fetchPlaceById(listPlace.place_id);

          if (cancelled) return;

          initializedPlaceIdRef.current = listPlace.id;
          setPlaceData({
            place: fetchedPlace,
            rating: fetchedPlace?.rating || 0,
            tags: fetchedPlace?.tags || [],
            notes: fetchedPlace?.notes || "",
          });

          // Center map (but don't set map place ID for read-only)
          if (fetchedPlace) {
            setMapCenterRef.current?.({
              lat: fetchedPlace.latitude,
              lng: fetchedPlace.longitude,
            });
          } else if (listPlace.latitude && listPlace.longitude) {
            setMapCenterRef.current?.({
              lat: listPlace.latitude,
              lng: listPlace.longitude,
            });
          }

          // Fetch photos from the list owner
          const fetchedPhotos = await fetchPhotosForPlaceByOwner(
            listPlace.place_id,
            ownerId,
          );

          if (cancelled) return;

          setPhotos(fetchedPhotos);
          setCurrentPhotoIndex(0);
          setIsLoadingPlace(false);
        } catch (error) {
          console.error("Error loading place data:", error);
          if (!cancelled) {
            setIsLoadingPlace(false);
          }
        }
      };

      loadData();

      return () => {
        cancelled = true;
      };
    } else {
      // Editable path: use existing logic
      const existingPlace = places.find((p) => p.id === listPlace.place_id);

      if (existingPlace) {
        initializedPlaceIdRef.current = listPlace.id;
        setPlaceData({
          place: existingPlace,
          rating: existingPlace.rating || 0,
          tags: existingPlace.tags || [],
          notes: existingPlace.notes || "",
        });
        setMapPlaceIdRef.current?.(existingPlace.google_place_id);
        setMapCenterRef.current?.({
          lat: existingPlace.latitude,
          lng: existingPlace.longitude,
        });

        fetchPhotosForPlace(existingPlace.id, profile!.id).then(
          (fetchedPhotos) => {
            setPhotos(fetchedPhotos);
            setCurrentPhotoIndex(0);
          },
        );
      } else {
        fetchPlace({
          userId: profile!.id,
          placeId: listPlace.google_place_id,
          lat: listPlace.latitude,
          lng: listPlace.longitude,
        }).then((fetchedPlace) => {
          if (fetchedPlace) {
            initializedPlaceIdRef.current = listPlace.id;
            setPlaceData({
              place: fetchedPlace,
              rating: fetchedPlace.rating || 0,
              tags: fetchedPlace.tags || [],
              notes: fetchedPlace.notes || "",
            });
            setMapPlaceIdRef.current?.(fetchedPlace.google_place_id);
            setMapCenterRef.current?.({
              lat: fetchedPlace.latitude,
              lng: fetchedPlace.longitude,
            });

            fetchPhotosForPlace(fetchedPlace.id, profile!.id).then(
              (fetchedPhotos) => {
                setPhotos(fetchedPhotos);
                setCurrentPhotoIndex(0);
              },
            );
          }
        });
      }
    }
  }, [
    isViewMode,
    isReadOnly,
    profile,
    listPlace,
    ownerId,
    places,
    fetchPlace,
    fetchPlaceById,
    fetchPhotosForPlace,
    fetchPhotosForPlaceByOwner,
  ]);

  // Loading state for add mode
  if (isAddMode && isLoadingGoogleDetails) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="200px"
      >
        <CircularProgress aria-label="Loading" role="status" />
        <Typography level="body-sm" sx={{ mt: 2 }}>
          Loading place details...
        </Typography>
      </Box>
    );
  }

  // Not found state for view mode
  if (isViewMode && !listPlace) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop={theme.spacing(10)}
      >
        <SentimentDissatisfiedIcon
          fontSize="large"
          data-testid="place-not-found-icon"
        />
        <Typography data-testid="place-not-found-message">
          Oops, place not found!
        </Typography>
      </Box>
    );
  }

  // Loading state for read-only path
  if (isViewMode && isReadOnly && isLoadingPlace) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        marginTop={theme.spacing(10)}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not found state for add mode
  if (isAddMode && !googlePlaceDetails) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop={theme.spacing(10)}
      >
        <SentimentDissatisfiedIcon
          fontSize="large"
          data-testid="place-not-found-icon"
        />
        <Typography data-testid="place-not-found-message">
          Could not load place details
        </Typography>
      </Box>
    );
  }

  // Get display values based on mode
  const placeName = isViewMode
    ? listPlace?.place_name || "Unnamed Place"
    : googlePlaceDetails?.displayName.text || "Unnamed Place";

  const placeAddress = isViewMode
    ? listPlace?.address
    : googlePlaceDetails?.formattedAddress;

  const placeCategory = isViewMode ? listPlace?.category : null;

  const handleRatingClick = async (newRating: number) => {
    if (isReadOnly) return;
    setPlaceData((prev) => ({ ...prev, rating: newRating }));

    // Only persist immediately in view mode
    if (isViewMode && place) {
      try {
        await updatePlace(place.id, { rating: newRating });
      } catch (error) {
        console.error("Failed to update rating:", error);
        setPlaceData((prev) => ({ ...prev, rating: place.rating || 0 }));
      }
    }
  };

  const handleNotesBlur = async () => {
    if (isReadOnly) return;
    if (isViewMode && place) {
      try {
        await updatePlace(place.id, { notes: notes || null });
      } catch (error) {
        console.error("Failed to update notes:", error);
        setPlaceData((prev) => ({ ...prev, notes: place.notes || "" }));
      }
    }
  };

  const handleAddTag = async () => {
    if (isReadOnly) return;
    if (!newTag.trim()) return;

    const trimmedTag = newTag.trim();
    if (tags.includes(trimmedTag)) {
      setNewTag("");
      setIsAddingTag(false);
      return;
    }

    const updatedTags = [...tags, trimmedTag];
    setPlaceData((prev) => ({ ...prev, tags: updatedTags }));
    setNewTag("");
    setIsAddingTag(false);

    // Only persist immediately in view mode
    if (isViewMode && place) {
      try {
        await updatePlace(place.id, { tags: updatedTags });
      } catch (error) {
        console.error("Failed to add tag:", error);
        setPlaceData((prev) => ({ ...prev, tags: place.tags || [] }));
      }
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    if (isReadOnly) return;
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    setPlaceData((prev) => ({ ...prev, tags: updatedTags }));

    // Only persist immediately in view mode
    if (isViewMode && place) {
      try {
        await updatePlace(place.id, { tags: updatedTags });
      } catch (error) {
        console.error("Failed to remove tag:", error);
        setPlaceData((prev) => ({ ...prev, tags: place.tags || [] }));
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || !profile) return;

    if (isAddMode) {
      // In add mode, just queue the files for upload on save
      const newPendingPhotos: PhotoFile[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        let exifData = null;
        try {
          exifData = await extractExifData(file);
        } catch (error) {
          console.warn("Could not extract EXIF data:", error);
        }
        newPendingPhotos.push({
          file,
          preview: URL.createObjectURL(file),
          exifData,
          name: file.name.replace(/\.[^/.]+$/, ""),
          caption: "",
          tags: [],
          isFavorite: false,
          uploading: false,
          uploaded: false,
          processingTimeline: false,
        });
      }
      setPendingPhotos((prev) => [...prev, ...newPendingPhotos]);
    } else {
      // In view mode, upload immediately
      if (!place) return;
      setIsUploading(true);

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          // Extract EXIF data
          let exifData = null;
          try {
            exifData = await extractExifData(file);
          } catch (error) {
            console.warn("Could not extract EXIF data:", error);
          }

          // Create PhotoFile object
          const photoFile = {
            file,
            preview: URL.createObjectURL(file),
            exifData,
            name: file.name.replace(/\.[^/.]+$/, ""),
            caption: "",
            tags: [],
            isFavorite: false,
            uploading: false,
            uploaded: false,
            processingTimeline: false,
          };

          // Upload photo
          await uploadPhoto(
            photoFile,
            profile!.id,
            listPlace!.list_id,
            place.id,
          );

          // Clean up preview URL
          URL.revokeObjectURL(photoFile.preview);
        }

        // Refresh photos list
        const updatedPhotos = await fetchPhotosForPlace(place.id, profile!.id);
        setPhotos(updatedPhotos);
        setCurrentPhotoIndex(0);
      } catch (error) {
        console.error("Failed to upload photo:", error);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (photos.length === 0) return;

    const photoToDelete = photos[currentPhotoIndex];

    try {
      await deletePhoto(photoToDelete.id);

      // Update local state
      const updatedPhotos = photos.filter(
        (_, idx) => idx !== currentPhotoIndex,
      );
      setPhotos(updatedPhotos);

      // Adjust current index if needed
      if (
        currentPhotoIndex >= updatedPhotos.length &&
        updatedPhotos.length > 0
      ) {
        setCurrentPhotoIndex(updatedPhotos.length - 1);
      } else if (updatedPhotos.length === 0) {
        setCurrentPhotoIndex(0);
      }
    } catch (error) {
      console.error("Failed to delete photo:", error);
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const handleRemovePendingPhoto = (index: number) => {
    setPendingPhotos((prev) => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.preview);
      const newPhotos = prev.filter((_, i) => i !== index);
      if (pendingPhotoIndex >= newPhotos.length && newPhotos.length > 0) {
        setPendingPhotoIndex(newPhotos.length - 1);
      } else if (newPhotos.length === 0) {
        setPendingPhotoIndex(0);
      }
      return newPhotos;
    });
  };

  const handleOpenSlideshow = () => {
    setSlideshowIndex(currentPhotoIndex);
    setSlideshowOpen(true);
  };

  const handleCloseSlideshow = () => {
    setSlideshowOpen(false);
  };

  const handleSlideshowPrev = () => {
    setSlideshowIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleSlideshowNext = () => {
    setSlideshowIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  // Save handler for add mode
  const handleSave = async () => {
    if (!isAddMode) return;
    if (!profile || !googlePlaceDetails) {
      alert("Unable to save - missing required data");
      return;
    }
    if (selectedLists.length === 0) {
      alert("Please select at least one list");
      return;
    }

    setIsSaving(true);

    try {
      const userId = profile!.id;

      // Extract country from address components
      const country =
        googlePlaceDetails.addressComponents?.find((c) =>
          c.types.includes("country"),
        )?.longText ?? null;

      // Create or fetch the place
      const newPlace = await createPlace({
        user_id: userId,
        latitude: googlePlaceDetails.location.latitude,
        longitude: googlePlaceDetails.location.longitude,
        name: googlePlaceDetails.displayName.text,
        address: googlePlaceDetails.formattedAddress,
        tags: tags,
        rating: rating || null,
        notes: notes || null,
        google_place_id: props.googlePlaceId,
        country,
      });

      if (!newPlace) {
        throw new Error("Failed to create place");
      }

      // Add place to all selected lists
      await Promise.all(
        selectedLists.map((lid) => addPlaceToList(newPlace, lid)),
      );

      // Upload any pending photos
      if (pendingPhotos.length > 0) {
        for (const photo of pendingPhotos) {
          await uploadPhoto(photo, userId, selectedLists[0], newPlace.id);
          URL.revokeObjectURL(photo.preview);
        }
      }

      // Call the completion callback
      props.onSaveComplete();
    } catch (error) {
      console.error("Failed to save place:", error);
      alert("Failed to save place. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleListSelectionChange = (value: string[] | null) => {
    const vals = Array.isArray(value) ? value : [];
    if (vals.includes("__create_new__")) {
      setCreateListModalOpen(true);
      setSelectedLists(vals.filter((v) => v !== "__create_new__"));
      return;
    }
    setSelectedLists(vals);
  };

  const handleCreateList = async (title: string) => {
    if (!profile) return;

    try {
      const newList = await createList({
        user_id: profile!.id,
        title,
        privacy_level: "public",
      });
      setSelectedLists((prev) => Array.from(new Set([...prev, newList.id])));
    } catch (err) {
      console.error("Failed to create list", err);
    } finally {
      setCreateListModalOpen(false);
    }
  };

  // Read-only "Add to My Lists" handlers
  const handleReadOnlyListSelectionChange = (value: string[]) => {
    if (value.includes("__create_new__")) {
      setCreateListForSaveModalOpen(true);
    } else {
      setReadOnlySelectedLists(value);
    }
  };

  const handleAddToMyLists = async () => {
    const userId = profile?.id;
    if (
      !userId ||
      !listPlace?.google_place_id ||
      readOnlySelectedLists.length === 0
    )
      return;

    setIsAddingToMyLists(true);
    try {
      let userPlace = await findPlaceByGoogleId(
        userId,
        listPlace.google_place_id,
      );

      if (!userPlace) {
        userPlace = await createPlace({
          user_id: userId,
          name: listPlace.place_name,
          address: listPlace.address,
          latitude: listPlace.latitude,
          longitude: listPlace.longitude,
          category: listPlace.category,
          google_place_id: listPlace.google_place_id,
          country: listPlace.country,
        });
      }

      for (const lid of readOnlySelectedLists) {
        await addPlaceToList(userPlace, lid);
      }

      setSuccessMessage(`Added to ${readOnlySelectedLists.length} list(s)`);
      setShowListSelector(false);
      setReadOnlySelectedLists([]);
    } catch (error) {
      console.error("Failed to add place:", error);
    } finally {
      setIsAddingToMyLists(false);
    }
  };

  const handleCreateListForSave = async (title: string) => {
    if (!profile) return;

    try {
      const newList = await useListStore.getState().createList({
        user_id: profile.id,
        title,
      });

      setCreateListForSaveModalOpen(false);
      await fetchLists(profile.id);
      setReadOnlySelectedLists([newList.id]);
    } catch (error) {
      console.error("Failed to create list:", error);
    }
  };

  // Use different data-testid prefixes based on read-only mode
  const testIdPrefix = isReadOnly ? "view-place" : "place";

  return (
    <Stack spacing={2}>
      {/* Header with place name and address */}
      <Stack paddingLeft={theme.spacing(2)} paddingRight={theme.spacing(2)}>
        <Typography
          level="h3"
          sx={{ fontWeight: theme.fontWeight.lg }}
          data-testid={`${testIdPrefix}-name`}
        >
          {placeName}
        </Typography>

        {placeAddress && (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <LocationOnIcon fontSize="small" sx={{ marginTop: "2px" }} />
            <Typography
              level="body-sm"
              sx={{ color: "text.secondary" }}
              data-testid={isReadOnly ? "view-place-address" : undefined}
            >
              {placeAddress}
            </Typography>
          </Stack>
        )}

        {placeCategory && (
          <Typography
            level="body-sm"
            sx={{
              marginTop: theme.spacing(1),
              paddingX: theme.spacing(1),
              paddingY: theme.spacing(0.5),
              backgroundColor: "background.level1",
              borderRadius: "sm",
              width: "fit-content",
            }}
            data-testid={isReadOnly ? "view-place-category" : undefined}
          >
            {placeCategory}
          </Typography>
        )}
      </Stack>

      {/* Photo Carousel (view mode) */}
      {isViewMode && (
        <PhotoUpload
          photos={photos.map(
            (p): PhotoItem => ({
              id: p.id,
              src: p.file_url,
              alt: p.file_name,
            }),
          )}
          currentIndex={currentPhotoIndex}
          onIndexChange={setCurrentPhotoIndex}
          onAddClick={isReadOnly ? undefined : handleUploadClick}
          onDeleteClick={isReadOnly ? undefined : handleDeleteClick}
          onPhotoClick={handleOpenSlideshow}
          isUploading={isUploading}
          emptyMessage={isReadOnly ? "No photos" : "No photos yet"}
        />
      )}

      {/* Pending photos preview for add mode */}
      {isAddMode && (
        <PhotoUpload
          photos={pendingPhotos.map(
            (p, i): PhotoItem => ({
              id: `pending-${i}`,
              src: p.preview,
              alt: p.name,
            }),
          )}
          currentIndex={pendingPhotoIndex}
          onIndexChange={setPendingPhotoIndex}
          onAddClick={handleUploadClick}
          onDeleteClick={
            pendingPhotos.length > 0
              ? () => handleRemovePendingPhoto(pendingPhotoIndex)
              : undefined
          }
          emptyMessage="Add photos"
        />
      )}

      {/* Hidden file input */}
      {!isReadOnly && (
        <input
          type="file"
          multiple
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      )}

      {/* Delete Confirmation Modal (editable only) */}
      {!isReadOnly && (
        <Modal
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <ModalDialog variant="outlined" role="alertdialog">
            <ModalClose />
            <Typography level="h4">Delete Photo</Typography>
            <Typography level="body-md">
              Are you sure you want to delete this photo? This action cannot be
              undone.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              marginTop={2}
            >
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      )}

      {/* Slideshow Modal */}
      <Modal
        open={slideshowOpen}
        onClose={handleCloseSlideshow}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
          }}
        >
          {/* Close button */}
          <IconButton
            variant="solid"
            size="lg"
            onClick={handleCloseSlideshow}
            data-testid={
              isReadOnly
                ? "view-place-slideshow-close"
                : "slideshow-close-button"
            }
            aria-label="Close slideshow"
            sx={{
              position: "absolute",
              top: theme.spacing(2),
              right: theme.spacing(2),
              zIndex: 2,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Main photo */}
          <Box
            component="img"
            src={photos[slideshowIndex]?.file_url}
            alt={photos[slideshowIndex]?.file_name}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />

          {/* Navigation buttons */}
          {photos.length > 1 && (
            <>
              <IconButton
                variant="solid"
                size="lg"
                onClick={handleSlideshowPrev}
                data-testid={
                  isReadOnly
                    ? "view-place-slideshow-prev"
                    : "slideshow-prev-button"
                }
                aria-label="Previous photo in slideshow"
                sx={{
                  position: "absolute",
                  left: theme.spacing(2),
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <IconButton
                variant="solid"
                size="lg"
                onClick={handleSlideshowNext}
                data-testid={
                  isReadOnly
                    ? "view-place-slideshow-next"
                    : "slideshow-next-button"
                }
                aria-label="Next photo in slideshow"
                sx={{
                  position: "absolute",
                  right: theme.spacing(2),
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {/* Photo counter */}
          <Typography
            level="body-md"
            sx={{
              position: "absolute",
              bottom: theme.spacing(2),
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              paddingX: theme.spacing(2),
              paddingY: theme.spacing(1),
              borderRadius: "md",
            }}
          >
            {slideshowIndex + 1} / {photos.length}
          </Typography>
        </Box>
      </Modal>

      <Divider />

      <Stack
        paddingLeft={theme.spacing(2)}
        paddingRight={theme.spacing(2)}
        spacing={2}
        sx={{ ...(isAddMode && isMobile && { pb: "60px" }) }}
      >
        {/* List Selection (add mode only) */}
        {isAddMode && (
          <Box>
            {isMobile ? (
              <Box>
                <FormLabel>Add to Lists</FormLabel>
                <List
                  size="sm"
                  sx={{
                    "--ListItem-radius": "8px",
                    maxHeight: "200px",
                    overflow: "auto",
                  }}
                >
                  {lists.map((list) => (
                    <ListItem key={list.id}>
                      <Checkbox
                        label={list.title}
                        checked={selectedLists.includes(list.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleListSelectionChange([
                              ...selectedLists,
                              list.id,
                            ]);
                          } else {
                            handleListSelectionChange(
                              selectedLists.filter((id) => id !== list.id),
                            );
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                  <ListItem>
                    <Button
                      variant="plain"
                      size="sm"
                      onClick={() => setCreateListModalOpen(true)}
                    >
                      + Create new list
                    </Button>
                  </ListItem>
                </List>
              </Box>
            ) : (
              <FormControl>
                <FormLabel>Add to Lists</FormLabel>
                <Select
                  multiple
                  placeholder="Select lists"
                  value={selectedLists}
                  onChange={(_, value) =>
                    handleListSelectionChange(value as string[])
                  }
                  slotProps={{
                    listbox: { sx: { maxHeight: "200px", overflow: "auto" } },
                  }}
                  renderValue={(selected) => (
                    <Box
                      sx={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}
                    >
                      {(selected || []).map((item) => {
                        const val =
                          typeof item === "string" ? item : item?.value;
                        const list = lists.find((l) => l.id === val);
                        return (
                          list && (
                            <Chip
                              key={val}
                              variant="soft"
                              color="primary"
                              size="sm"
                            >
                              {list.title}
                            </Chip>
                          )
                        );
                      })}
                    </Box>
                  )}
                >
                  {lists.map((list) => (
                    <Option key={list.id} value={list.id}>
                      {list.title}
                    </Option>
                  ))}
                  <Option
                    value="__create_new__"
                    sx={{ color: "primary.main", fontWeight: 600 }}
                  >
                    + Create new list
                  </Option>
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Rating */}
        {isReadOnly ? (
          rating > 0 && (
            <Box>
              <Typography
                level="body-sm"
                sx={{
                  fontWeight: theme.fontWeight.md,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Rating
              </Typography>
              <Box data-testid="view-place-rating">
                <Rating value={rating} precision={0.5} readOnly />
              </Box>
            </Box>
          )
        ) : (
          <Box>
            <Typography
              level="body-sm"
              sx={{
                fontWeight: theme.fontWeight.md,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Rating
            </Typography>
            <Rating
              value={rating}
              precision={0.5}
              onChange={(_, newValue) => handleRatingClick(newValue ?? 0)}
            />
          </Box>
        )}

        {/* Notes */}
        {isReadOnly ? (
          notes && (
            <Box>
              <Typography
                level="body-sm"
                sx={{
                  fontWeight: theme.fontWeight.md,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Notes
              </Typography>
              <Typography
                level="body-sm"
                sx={{ whiteSpace: "pre-line" }}
                data-testid="view-place-notes"
              >
                {notes}
              </Typography>
            </Box>
          )
        ) : (
          <Box>
            <Typography
              level="body-sm"
              sx={{
                fontWeight: theme.fontWeight.md,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Notes
            </Typography>
            <Textarea
              placeholder="Add notes..."
              value={notes}
              onChange={(e) =>
                setPlaceData((prev) => ({ ...prev, notes: e.target.value }))
              }
              onBlur={handleNotesBlur}
              minRows={2}
              maxRows={6}
              sx={{ fontSize: "sm" }}
            />
          </Box>
        )}

        {/* Tags */}
        {isReadOnly ? (
          tags.length > 0 && (
            <Box>
              <Typography
                level="body-sm"
                sx={{
                  fontWeight: theme.fontWeight.md,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Tags
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                data-testid="view-place-tags"
              >
                {tags.map((tag) => (
                  <Chip key={tag} variant="soft" size="sm">
                    {tag}
                  </Chip>
                ))}
              </Stack>
            </Box>
          )
        ) : (
          <Box>
            <Typography
              level="body-sm"
              sx={{
                fontWeight: theme.fontWeight.md,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Tags
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  variant="soft"
                  size="sm"
                  slotProps={{
                    endDecorator: {
                      sx: { pointerEvents: "auto" },
                    },
                  }}
                  endDecorator={
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => handleRemoveTag(tag)}
                      sx={{ minWidth: "auto", minHeight: "auto", p: 0 }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  }
                >
                  {tag}
                </Chip>
              ))}
              {isAddingTag ? (
                <Input
                  size="sm"
                  placeholder="Add tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddTag();
                    } else if (e.key === "Escape") {
                      setNewTag("");
                      setIsAddingTag(false);
                    }
                  }}
                  onBlur={() => {
                    if (newTag.trim()) {
                      handleAddTag();
                    } else {
                      setIsAddingTag(false);
                    }
                  }}
                  autoFocus
                  sx={{ width: "120px" }}
                />
              ) : (
                <Chip
                  variant="outlined"
                  size="sm"
                  startDecorator={<AddIcon fontSize="small" />}
                  onClick={() => setIsAddingTag(true)}
                  sx={{ cursor: "pointer" }}
                >
                  Add tag
                </Chip>
              )}
            </Stack>
          </Box>
        )}

        {/* View mode specific fields */}
        {isViewMode && listPlace?.visit_date && (
          <Box>
            <Typography
              level="body-sm"
              sx={{ fontWeight: theme.fontWeight.md }}
            >
              Visit Date
            </Typography>
            <Typography
              level="body-sm"
              data-testid={isReadOnly ? "view-place-visit-date" : undefined}
            >
              {new Date(listPlace.visit_date).toLocaleDateString()}
            </Typography>
          </Box>
        )}

        {isViewMode &&
          listPlace &&
          listPlace.latitude !== null &&
          listPlace.longitude !== null && (
            <Box>
              <Typography
                level="body-sm"
                sx={{ fontWeight: theme.fontWeight.md }}
              >
                Coordinates
              </Typography>
              <Typography
                level="body-sm"
                sx={{ fontFamily: "monospace" }}
                data-testid={isReadOnly ? "view-place-coordinates" : undefined}
              >
                {listPlace.latitude.toFixed(6)},{" "}
                {listPlace.longitude.toFixed(6)}
              </Typography>
            </Box>
          )}

        {/* Add to My Lists (read-only only) */}
        {isReadOnly && listPlace?.google_place_id && profile && (
          <Box>
            {!showListSelector ? (
              <Button
                variant="soft"
                color="primary"
                startDecorator={<AddIcon />}
                onClick={() => setShowListSelector(true)}
                data-testid="add-to-my-lists-button"
              >
                Add to My Lists
              </Button>
            ) : (
              <Stack spacing={1}>
                {isMobile ? (
                  <Box>
                    <FormLabel>Select Lists</FormLabel>
                    <List
                      size="sm"
                      sx={{
                        "--ListItem-radius": "8px",
                        maxHeight: "200px",
                        overflow: "auto",
                      }}
                    >
                      {lists.map((list) => (
                        <ListItem key={list.id}>
                          <Checkbox
                            label={list.title}
                            checked={readOnlySelectedLists.includes(list.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleReadOnlyListSelectionChange([
                                  ...readOnlySelectedLists,
                                  list.id,
                                ]);
                              } else {
                                handleReadOnlyListSelectionChange(
                                  readOnlySelectedLists.filter(
                                    (id) => id !== list.id,
                                  ),
                                );
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                      <ListItem>
                        <Button
                          variant="plain"
                          size="sm"
                          onClick={() => setCreateListModalOpen(true)}
                        >
                          + Create new list
                        </Button>
                      </ListItem>
                    </List>
                  </Box>
                ) : (
                  <FormControl>
                    <FormLabel>Select Lists</FormLabel>
                    <Select
                      multiple
                      placeholder="Choose lists"
                      value={readOnlySelectedLists}
                      onChange={(_, value) =>
                        handleReadOnlyListSelectionChange(value as string[])
                      }
                      slotProps={{
                        listbox: {
                          sx: { maxHeight: "200px", overflow: "auto" },
                        },
                      }}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {selected.map((option) => {
                            const list = lists.find(
                              (l) => l.id === option.value,
                            );
                            return (
                              <Chip key={option.value} variant="soft" size="sm">
                                {list?.title || option.value}
                              </Chip>
                            );
                          })}
                        </Box>
                      )}
                    >
                      {lists.map((list) => (
                        <Option key={list.id} value={list.id}>
                          {list.title}
                        </Option>
                      ))}
                      <Option value="__create_new__">+ Create new list</Option>
                    </Select>
                  </FormControl>
                )}
                <Stack direction="row" spacing={1}>
                  <Button
                    loading={isAddingToMyLists}
                    disabled={readOnlySelectedLists.length === 0}
                    onClick={handleAddToMyLists}
                  >
                    Add
                  </Button>
                  <Button
                    variant="plain"
                    onClick={() => {
                      setShowListSelector(false);
                      setReadOnlySelectedLists([]);
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        )}

        {/* Save button (add mode, desktop only - mobile uses sticky below) */}
        {isAddMode && !isMobile && (
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={selectedLists.length === 0}
            fullWidth
          >
            {isSaving ? "Saving..." : "Save Place"}
          </Button>
        )}
      </Stack>

      {/* Sticky Save button (add mode, mobile only) */}
      {isAddMode && isMobile && (
        <Box
          sx={{
            position: "sticky",
            bottom: `${BOTTOM_NAV_HEIGHT}px`,
            bgcolor: "background.surface",
            py: 1.5,
            px: 2,
            boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
            zIndex: 1,
          }}
        >
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={selectedLists.length === 0}
            fullWidth
          >
            {isSaving ? "Saving..." : "Save Place"}
          </Button>
        </Box>
      )}

      {/* Create List Modal (add mode) */}
      <CreateListModal
        open={createListModalOpen}
        onClose={() => setCreateListModalOpen(false)}
        onCreate={handleCreateList}
      />

      {/* Create List Modal (read-only mode) */}
      <CreateListModal
        open={createListForSaveModalOpen}
        onClose={() => setCreateListForSaveModalOpen(false)}
        onCreate={handleCreateListForSave}
      />

      {/* Success Snackbar (read-only mode) */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
      >
        {successMessage}
      </Snackbar>
    </Stack>
  );
};
